/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {AdminResourceItemRoutePageComponent} from '../common/controller/admin-resource-item-route-page.component';
import {adminResourceItemRouteResolve} from '../common/controller/admin-resource-routing';
import {getRoutePageComponent} from '../common/controller/route-page-component-registry';
import {collectStatesFromProvider, AdnRouterHelperApi} from '../routing/state-utils';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {getResourceItemTitleResolves, getResourceItemTitleView} from '../base/default-item-title-view';
import {WorkspaceViewPageComponent} from './workspace-view-page.component';
import {ResourceSidebarActionsComponent} from '../base/resource-sidebar-actions.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {WorkspaceResource} from '../../components/api/resources/resource/workspace.resource';


const workspaceTitleResolves = Object.assign({}, getResourceItemTitleResolves('Workspace'), {
  titleVariant: function() {
    return 'workspace';
  }
});

const workspaceTitleView = getResourceItemTitleView();

export function workspaceStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  return collectStatesFromProvider(function register($stateProvider) {
    $stateProvider.state('app.wor.workspaces', {
      url: '/workspace',
      resolve: {
        ResourceFactory: function() {
          return adnRouterHelper.services.resource(WorkspaceResource);
        },
        readOnly: function() {
          return false;
        },
        listNewStateName: function() {
          return 'app.wor.workspaces.new';
        }
      },
      views: {
        'content@app': {
          component: getRoutePageComponent('workspace-route')
        },
        'context@app': {
          component: ListDefaultActionsComponent,
          bindings: {
            readOnly: 'readOnly',
            newStateName: 'listNewStateName'
          }
        },
        'title@app': stateTitleView()
      },
      data: {
        title: 'Dashboards'
      }
    }).state('app.wor.workspaces.new', {
      url: '/new',
      resolve: Object.assign({}, workspaceTitleResolves, adminResourceItemRouteResolve('workspace-edit', 'edit'), {
        model: /*@ngInject*/ function() {
          const Workspace = adnRouterHelper.services.resource(WorkspaceResource);
          return new Workspace();
        },
        workspace: /*@ngInject*/ function(model) {
          return model;
        },
        readOnly: /*@ngInject*/ function(workspace) {
          const LocalUserPermissions = adnRouterHelper.services.localUserPermissions();

          return !LocalUserPermissions.hasNetworkPermission('MANAGE_NETWORK_DASHBOARDS')
            && workspace.type !== 'PERSONAL';
        },
        modelComms: /*@ngInject*/ function() {
          return adnRouterHelper.createModelComms();
        }
      }),
      views: {
        'content@app': {
          component: AdminResourceItemRoutePageComponent,
          bindings: { item: 'adminResourceItemKey', variant: 'adminResourceItemVariant' }
        },
        'context@app': {},
        'title@app': workspaceTitleView
      },
      data: {
        editable: true,
        title: 'New Dashboard'
      }
    }).state('app.wor.workspaces.workspace', {
      url: '/:id',
      resolve: Object.assign({}, workspaceTitleResolves, {
        model: /*@ngInject*/ function($stateParams) {
          return adnRouterHelper.services.resource(WorkspaceResource).get({id: $stateParams.id}).$promise;
        },
        workspace: /*@ngInject*/ function(model) {
          return model;
        },
        modelComms: /*@ngInject*/ function() {
          return adnRouterHelper.createModelComms();
        }
      }),
      views: {
        'content@app': {
          component: WorkspaceViewPageComponent,
          bindings: {model: 'model'}
        },
        'context@app': {
          component: ResourceSidebarActionsComponent,
          bindings: {readOnly: 'readOnly'}
        },
        'title@app': workspaceTitleView
      },
      data: {
        editable: false,
        title: '{{workspace.name}}'
      }
    }).state('app.wor.workspaces.workspace.edit', {
      url: '/edit',
      resolve: adminResourceItemRouteResolve('workspace-edit', 'edit'),
      views: {
        'content@app': {
          component: AdminResourceItemRoutePageComponent,
          bindings: { item: 'adminResourceItemKey', variant: 'adminResourceItemVariant' }
        },
        'context@app': {},
        'title@app': workspaceTitleView
      },
      data: {
        editable: true,
        title: 'Dashboard'
      }
    });
  }, adnRouterHelper);
}
